import { LitElement, html, css, nothing } from "lit";
import { customElement, property } from "lit/decorators.js";
import type { ConversationSummary } from "../widget/state";
import { formatRelativeTime } from "../utils/time";
import { injectInterFont } from "../utils/fonts";

@customElement("cg-recent-chats")
export class CGRecentChats extends LitElement {
  static override styles = css`
    :host {
      --cg-font: "Inter", sans-serif;
      display: block;
      height: 100%;
      background: var(--cg-bg);
      font-family: "Inter", sans-serif !important;
    }

    *,
    *::before,
    *::after {
      font-family: "Inter", sans-serif !important;
    }

    .wrap {
      height: 100%;
      overflow-y: auto;
      padding: 12px 0;
      box-sizing: border-box;
      font-family: "Inter", sans-serif !important;
    }

    /* =========================
       Header
    ========================== */

    .header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 6px 14px 12px;
    }

    .title {
      font-family: "Inter", sans-serif !important;
      font-size: 15px;
      font-weight: 700;
    }

    /* =========================
       List
    ========================== */

    .list {
      display: flex;
      flex-direction: column;
    }

    .row {
      font-family: "Inter", sans-serif !important;
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 12px 14px;
      cursor: pointer;
      background: transparent;
      border: 0;
      text-align: left;
      transition: background 120ms ease;
    }

    .row:hover {
      background: color-mix(in srgb, var(--cg-text) 4%, transparent);
    }

    .row.active {
      background: color-mix(in srgb, var(--cg-text) 8%, transparent);
    }

    .divider {
      height: 1px;
      background: var(--cg-border);
      margin-left: 56px;
    }

    /* =========================
       Avatar
    ========================== */

    .avatar {
      width: 34px;
      height: 34px;
      border-radius: 999px;
      background: var(--cg-surface);
      border: 1px solid var(--cg-border);
      color: var(--cg-text);
      display: grid;
      place-items: center;
      font-weight: 700;
      font-size: 14px;
      flex-shrink: 0;
    }

    /* =========================
       Content
    ========================== */

    .meta {
      min-width: 0;
      flex: 1;
    }

    .title-text {
      font-family: "Inter", sans-serif !important;
      font-size: 14px;
      font-weight: 500;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      line-height: 1.3;
    }

    .sub {
      font-family: "Inter", sans-serif !important;
      margin-top: 2px;
      font-size: 12px;
      color: var(--cg-muted);
      display: flex;
      gap: 6px;
      align-items: center;
    }

    .dot {
      opacity: 0.6;
    }

    /* =========================
       Right Actions
    ========================== */

    .right {
      display: flex;
      align-items: center;
      gap: 8px;
      flex-shrink: 0;
    }

    .pill {
      font-family: "Inter", sans-serif !important;
      font-size: 11px;
      padding: 3px 8px;
      border-radius: 999px;
      background: rgba(34, 197, 94, 0.12);
      color: #16a34a;
      font-weight: 600;
    }

    .chevron {
      font-size: 18px;
      opacity: 0.5;
    }

    .status-icon {
      font-size: 14px;
      color: var(--cg-muted);
      display: flex;
      align-items: center;
      justify-content: center;
      opacity: 0.7;
    }

    /* =========================
       Empty State
    ========================== */

    .empty {
      font-family: "Inter", sans-serif !important;
      padding: 48px 20px;
      text-align: center;
      color: var(--cg-muted);
      font-size: 13px;
    }

    button {
      color: inherit;
      font: inherit;
      font-family: "Inter", sans-serif !important;
      font-synthesis: none;
      letter-spacing: 0;
    }

    .row,
    .row *,
    .header,
    .header *,
    .empty {
      font-family: "Inter", sans-serif !important;
      font-synthesis: none;
      letter-spacing: 0;
    }
  `;

  @property({ type: Array }) items: ConversationSummary[] = [];
  @property({ type: String }) activeId?: string;
  @property({ type: String }) agentName?: string;
  @property({ type: String }) agentAvatar?: string;

  @property({ attribute: false }) onSelect?: (id: string) => void;

  override connectedCallback(): void {
    super.connectedCallback();
    injectInterFont(this.shadowRoot ?? this);
  }

  override render() {
    return html`
      <div class="wrap">
        <div class="header">
          <div class="title">Recent chats</div>
        </div>

        ${this.items.length === 0
          ? html` <div class="empty">No conversations yet.</div> `
          : html`
              <div class="list">
                ${this.items.map(
                  (c, i) => html`
                    <button
                      class="row ${c.id === this.activeId ? "active" : ""}"
                      @click=${() => this.onSelect?.(c.id)}
                    >
                      <div class="avatar">
                        ${this.agentAvatar
                          ? html`<img
                              src=${this.agentAvatar}
                              style="width:100%; height:100%; object-fit:cover; border-radius:999px;"
                            />`
                          : (this.agentName?.[0] ?? "C").toUpperCase()}
                      </div>

                      <div class="meta">
                        <div
                          class="title-text"
                          style="font-family: Inter, sans-serif !important; font-synthesis: none; letter-spacing: 0;"
                        >
                          ${c.title || "Untitled chat"}
                        </div>

                        <div class="sub">
                          <span>${this.agentName || "AI Agent"}</span>
                          <span class="dot">•</span>
                          <span>${formatRelativeTime(c.updatedAt)}</span>
                        </div>
                      </div>

                      <div class="right">
                        ${String(c.status).toUpperCase() === "CLOSED"
                          ? html`<div class="status-icon" title="Closed">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="14"
                                height="14"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                              >
                                <rect
                                  x="3"
                                  y="11"
                                  width="18"
                                  height="11"
                                  rx="2"
                                  ry="2"
                                />
                                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                              </svg>
                            </div>`
                          : nothing}
                        ${c.id === this.activeId
                          ? html`<div class="pill">Open</div>`
                          : nothing}
                        <div class="chevron">›</div>
                      </div>
                    </button>

                    ${i < this.items.length - 1
                      ? html`<div class="divider"></div>`
                      : nothing}
                  `,
                )}
              </div>
            `}
      </div>
    `;
  }
}
